import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './LinePaymentModal.css';

// For staging/dev use staging URLs; for production use prod URLs
const LINE_CHECKOUT_ORIGIN =
  process.env.REACT_APP_LINE_CHECKOUT_ORIGIN || 'https://staging.checkout.line.net.ar';
// Use versioned script for stability (v1.0.0 recommended for production)
// For staging/dev, use latest; for production, use versioned with SRI
const LINE_CHECKOUT_SCRIPT_URL =
  process.env.REACT_APP_LINE_CHECKOUT_SCRIPT_URL ||
  'https://staging.checkout.line.net.ar/line-checkout-loader.js';
// SRI hash for production (obtain from manifest.json when deploying to prod)
const LINE_CHECKOUT_SCRIPT_INTEGRITY = process.env.REACT_APP_LINE_CHECKOUT_SCRIPT_SRI || null;

/**
 * LinePaymentModal - Modal para pagos con Line Checkout
 *
 * Props:
 * - isOpen: boolean - Si el modal está abierto
 * - onClose: function - Callback al cerrar
 * - paymentId: string - ID del pago creado en backend (flujo seguro, recomendado)
 * - amount: number - Monto (solo para mostrar en UI)
 * - orderId: string - ID de la orden (solo para verificación)
 * - qr: string - QR code data (fallback si widget no carga)
 * - deeplink: string - Deep link para apps móviles (fallback)
 * - onSuccess: function - Callback cuando el pago es exitoso
 * - onVerifyPayment: function - Callback para verificar pago en backend
 */
function LinePaymentModal({
  isOpen,
  onClose,
  paymentId,
  amount,
  orderId,
  qr,
  deeplink,
  onSuccess,
  onVerifyPayment,
}) {
  const { t } = useTranslation();
  const widgetContainerRef = useRef(null);
  const scriptRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showQrFallback, setShowQrFallback] = useState(false);

  const publicKey =
    process.env.REACT_APP_LINE_PUBLIC_KEY ||
    'pk_sandbox_modo_5ef1f76c63582c4865179b8872ef9711b0371721c7f36ee71fb5e23d083d1b0e';

  // Handle postMessage events from Line Checkout
  const handleMessage = useCallback(
    async (event) => {
      // Validate origin
      if (event.origin !== LINE_CHECKOUT_ORIGIN) return;

      console.log('LinePaymentModal: Received message:', event.data);

      switch (event.data.type) {
        case 'line-checkout:ready':
          console.log('LinePaymentModal: Widget ready');
          setLoading(false);
          break;

        case 'line-checkout:payment-success':
          console.log('LinePaymentModal: Payment success', event.data.payload);
          setLoading(true);

          // Verify payment on backend before completing
          if (onVerifyPayment) {
            try {
              const verified = await onVerifyPayment({
                paymentId: event.data.payload.paymentId,
                orderId: orderId,
                amount: amount,
              });

              if (verified) {
                onSuccess?.(event.data.payload);
                onClose();
              } else {
                setError(t('payment.verificationFailed', 'No se pudo verificar el pago'));
                setLoading(false);
              }
            } catch (err) {
              console.error('LinePaymentModal: Verification error:', err);
              setError(t('payment.verificationError', 'Error verificando el pago'));
              setLoading(false);
            }
          } else {
            // No verification callback, just complete
            onSuccess?.(event.data.payload);
            onClose();
          }
          break;

        case 'line-checkout:payment-failure':
          console.error('LinePaymentModal: Payment failed', event.data.payload);
          setError(
            event.data.payload?.error?.message ||
              t('payment.failed', 'El pago no pudo completarse')
          );
          setLoading(false);
          break;

        case 'line-checkout:payment-cancelled':
          console.log('LinePaymentModal: Payment cancelled');
          setLoading(false);
          onClose();
          break;

        case 'line-checkout:payment-started':
          console.log('LinePaymentModal: Payment started');
          break;

        default:
          break;
      }
    },
    [orderId, amount, onSuccess, onClose, onVerifyPayment, t]
  );

  useEffect(() => {
    if (!isOpen) return;

    // Reset state when opening
    setError(null);
    setLoading(true);

    console.log('LinePaymentModal: Opening modal');
    console.log('LinePaymentModal: PaymentId:', paymentId);
    console.log('LinePaymentModal: Amount:', amount);
    console.log('LinePaymentModal: OrderId:', orderId);

    if (!paymentId) {
      console.error('LinePaymentModal: No paymentId provided');
      setError(t('payment.noPaymentId', 'No se pudo iniciar el pago'));
      setLoading(false);
      return;
    }

    // Add message listener for postMessage events
    window.addEventListener('message', handleMessage);

    // Check if LineCheckout is already loaded
    if (window.LineCheckout) {
      console.log('LinePaymentModal: LineCheckout already loaded');
      initializeWidget();
      return;
    }

    // Load Line Checkout loader script
    console.log('LinePaymentModal: Loading line-checkout-loader.js...');
    const script = document.createElement('script');
    script.src = LINE_CHECKOUT_SCRIPT_URL;
    script.async = true;

    // Add SRI (Subresource Integrity) for security in production
    if (LINE_CHECKOUT_SCRIPT_INTEGRITY) {
      script.integrity = LINE_CHECKOUT_SCRIPT_INTEGRITY;
      script.crossOrigin = 'anonymous';
    }

    // Add data attributes for the secure flow (paymentId)
    script.dataset.publicKey = publicKey;
    script.dataset.paymentId = paymentId;
    script.dataset.container = 'line-checkout-container';

    script.onload = () => {
      console.log('LinePaymentModal: Script loaded');
      console.log('LinePaymentModal: window.LineCheckout available:', !!window.LineCheckout);

      // Small delay to ensure LineCheckout is fully initialized
      setTimeout(initializeWidget, 100);
    };

    script.onerror = () => {
      console.error('LinePaymentModal: Error loading script');
      // If QR data is available, show fallback instead of error
      if (qr) {
        console.log('LinePaymentModal: Showing QR fallback');
        setShowQrFallback(true);
        setLoading(false);
      } else {
        setError(t('payment.loadError', 'Error al cargar el widget de pago'));
        setLoading(false);
      }
    };

    scriptRef.current = script;
    document.body.appendChild(script);

    return () => {
      window.removeEventListener('message', handleMessage);
      if (scriptRef.current && document.body.contains(scriptRef.current)) {
        document.body.removeChild(scriptRef.current);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, paymentId, publicKey, qr, handleMessage, t]);

  const initializeWidget = () => {
    console.log('LinePaymentModal: Initializing widget...');

    try {
      if (!window.LineCheckout) {
        console.error('LinePaymentModal: LineCheckout not available');
        setError(t('payment.widgetNotAvailable', 'Widget de pago no disponible'));
        setLoading(false);
        return;
      }

      if (!widgetContainerRef.current) {
        console.error('LinePaymentModal: Container ref not available');
        setError(t('payment.containerError', 'Contenedor no disponible'));
        setLoading(false);
        return;
      }

      if (!paymentId) {
        console.error('LinePaymentModal: No paymentId');
        setError(t('payment.noPaymentId', 'No se pudo iniciar el pago'));
        setLoading(false);
        return;
      }

      const baseUrl = window.location.origin;
      const returnPath = window.location.pathname;

      // Secure flow config - only paymentId, no amount/orderId (already in payment)
      const config = {
        containerId: 'line-checkout-container',
        publicKey: publicKey,
        paymentId: paymentId,

        // Mobile callback URLs
        callbackURLSuccess: `${baseUrl}${returnPath}?payment=success&paymentId=${paymentId}`,
        callbackURLFailure: `${baseUrl}${returnPath}?payment=failure&paymentId=${paymentId}`,

        // Debug mode in development
        debug: process.env.NODE_ENV === 'development',

        // Callbacks (in addition to postMessage)
        onSuccess: (payload) => {
          console.log('LinePaymentModal: onSuccess callback:', payload);
        },
        onFailure: (err) => {
          console.error('LinePaymentModal: onFailure callback:', err);
        },
        onCancel: () => {
          console.log('LinePaymentModal: onCancel callback');
        },
      };

      console.log('LinePaymentModal: Initializing with config:', config);
      window.LineCheckout.init(config);

      // Set loading false after a short delay
      setTimeout(() => setLoading(false), 500);
    } catch (err) {
      console.error('LinePaymentModal: Error initializing widget:', err);
      setError(t('payment.initError', 'Error al inicializar el widget de pago'));
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="line-payment-modal-overlay" onClick={onClose}>
      <div className="line-payment-modal" onClick={(e) => e.stopPropagation()}>
        <div className="line-payment-header">
          <h2>{t('payment.title')}</h2>
          <button className="close-btn" onClick={onClose}>
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        </div>

        <div className="line-payment-content">
          {loading && (
            <div className="payment-loading">
              <div className="spinner"></div>
              <p>{t('payment.loading', 'Cargando método de pago...')}</p>
            </div>
          )}

          {error && !showQrFallback && (
            <div className="payment-error">
              <span className="material-icons" aria-hidden="true">error</span>
              <p>{error}</p>
              <button className="btn-primary" onClick={onClose}>
                {t('common.close', 'Cerrar')}
              </button>
            </div>
          )}

          {/* QR Fallback when widget fails but QR data is available */}
          {showQrFallback && qr && (
            <div className="payment-qr-fallback">
              <p>{t('payment.scanQr', 'Escaneá el código QR con tu app de pagos')}</p>
              <div className="qr-container">
                <img src={qr} alt="QR Code de pago" className="payment-qr-image" />
              </div>
              {deeplink && (
                <a
                  href={deeplink}
                  className="btn-secondary deeplink-btn"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('payment.openApp', 'Abrir app de pagos')}
                </a>
              )}
              <p className="qr-note">
                {t('payment.qrNote', 'Una vez completado el pago, esta pantalla se actualizará automáticamente')}
              </p>
            </div>
          )}

          {/* Line Checkout Container */}
          <div
            id="line-checkout-container"
            ref={widgetContainerRef}
            className="line-checkout-container"
            style={{ display: loading || error ? 'none' : 'block' }}
          ></div>
        </div>

        <div className="line-payment-footer">
          <div className="payment-info">
            {amount && (
              <div className="payment-amount">
                <span>{t('payment.total', 'Total a pagar')}:</span>
                <strong>${amount.toFixed(2)}</strong>
              </div>
            )}
            <p className="payment-note">
              <span className="material-icons" aria-hidden="true">lock</span>
              {t('payment.secure')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

export default LinePaymentModal;
